import { useState } from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Store } from 'lucide-react';
import { api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { brandImageUrl } from '@/components/brands/BrandCard';

const fetcher = (url) => api.get(url).then((res) => res.data);

export default function BrandProfile({ user }) {
  const { slug } = useParams();
  const darkMode = useDarkMode();
  const isDark = darkMode.isDark;
  const { data: brand, error, isLoading } = useSWR(`/brands/${slug}`, fetcher);
  const [activePhoto, setActivePhoto] = useState(0);

  useDocumentMeta({
    title: brand ? `${brand.brand_name} — Support a Brand, Mementa` : 'Support a Brand — Mementa',
    description: brand?.story?.slice(0, 150) || 'Discover small businesses on Mementa.',
    url: `https://mementa.gifts/brands/${slug}`,
  });

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: darkMode.bgPrimary }}>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center" data-testid="brand-not-found">
            <Store size={48} className="mx-auto mb-4" style={{ color: '#9E6752', opacity: 0.5 }} />
            <h1 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}>
              Brand not found
            </h1>
            <p className="text-sm mb-6" style={{ color: darkMode.textSecondary }}>
              This brand may no longer be in the directory.
            </p>
            <Link
              to="/brands"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
              style={{ background: '#534145', color: '#FFFAF5' }}
            >
              <ArrowLeft size={16} /> Back to the directory
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isLoading || !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: darkMode.bgPrimary }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-4" style={{ borderColor: '#534145' }} />
      </div>
    );
  }

  const photos = brand.image_paths || [];

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <Link
          to="/brands"
          data-testid="brand-back-link"
          className="inline-flex items-center gap-1.5 text-sm mb-8 transition-opacity hover:opacity-70"
          style={{ color: isDark ? '#D4A4A8' : '#9E6752' }}
        >
          <ArrowLeft size={15} /> All brands
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start mb-14">
          {/* Photo gallery */}
          <div>
            <div
              className="relative overflow-hidden rounded-2xl mb-3"
              style={{ aspectRatio: '4 / 3', background: isDark ? '#1E2A36' : '#F7F0EA' }}
            >
              {photos.length > 0 ? (
                <img
                  src={brandImageUrl(photos[activePhoto])}
                  alt={brand.brand_name}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="brand-hero-photo"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Store size={56} style={{ color: '#534145', opacity: 0.3 }} />
                </div>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2">
                {photos.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => setActivePhoto(i)}
                    data-testid={`brand-photo-thumb-${i}`}
                    className="relative overflow-hidden rounded-lg transition-all"
                    style={{
                      width: 64,
                      height: 64,
                      border: `2px solid ${i === activePhoto ? '#534145' : 'transparent'}`,
                      opacity: i === activePhoto ? 1 : 0.65,
                    }}
                  >
                    <img src={brandImageUrl(p)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Story */}
          <div>
            {brand.category && (
              <span
                className="inline-block text-[10px] uppercase tracking-[0.16em] font-bold px-3 py-1 rounded-full mb-4"
                style={{
                  color: isDark ? '#D4A4A8' : '#3F2F31',
                  background: isDark ? 'rgba(212,164,168,0.15)' : 'rgba(83,65,69,0.08)',
                  border: `1px solid ${isDark ? 'rgba(212,164,168,0.4)' : 'rgba(83,65,69,0.25)'}`,
                }}
              >
                {brand.category}
              </span>
            )}
            <h1
              className="mb-2"
              style={{
                fontFamily: "'Bodoni Moda', serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                lineHeight: 1.1,
                color: isDark ? '#FFFAF5' : '#20212B',
              }}
              data-testid="brand-profile-name"
            >
              {brand.brand_name}
            </h1>
            {brand.founder_name && (
              <p className="text-sm mb-5" style={{ color: isDark ? '#D4A4A8' : '#9E6752' }}>
                Founded by {brand.founder_name}
              </p>
            )}
            <p
              className="text-base leading-relaxed mb-7 whitespace-pre-line"
              style={{
                fontFamily: 'Playfair Display, serif',
                color: isDark ? 'rgba(255,250,245,0.85)' : 'rgba(32,33,43,0.9)',
              }}
              data-testid="brand-profile-story"
            >
              {brand.story}
            </p>
            {brand.website && (
              <a
                href={brand.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brand-shop-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
                style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
              >
                Shop {brand.brand_name}
                <ExternalLink size={15} />
              </a>
            )}
          </div>
        </div>

        {/* Linked Mementa products */}
        {(brand.products || []).length > 0 && (
          <div data-testid="brand-linked-products">
            <div className="mb-5">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2"
                style={{ color: isDark ? '#D4A4A8' : '#3F2F31' }}
              >
                On Mementa
              </div>
              <h2
                style={{
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 600,
                  fontSize: 'clamp(1.4rem, 3vw, 1.9rem)',
                  color: isDark ? '#FFFAF5' : '#20212B',
                }}
              >
                Their gifts in our catalog
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {brand.products.map((product) => (
                <ProductCard key={product.id} product={product} user={user} source="brand" />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
